import { memo, useEffect, useState } from "react";
import {
  Alert,
  Button,
  Collapse,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  TextField,
  MenuItem,
} from "@mui/material";
import { Close as CloseIcon } from "components/icons";
import MDBox from "components/MDBox";
import { createRisk } from "app/services/iqms.service";

const RISK_CATEGORIES = ["TECHNICAL", "OPERATIONAL", "FINANCIAL", "COMPLIANCE", "SECURITY", "RESOURCE"];
const SEVERITY_LEVELS = ["LOW", "MEDIUM", "HIGH", "CRITICAL"];
const RISK_STATUSES = ["IDENTIFIED", "ASSESSED", "MITIGATED", "CLOSED", "ACCEPTED"];

const initialData = {
  id: null,
  riskCode: "",
  riskTitle: "",
  description: "",
  category: "TECHNICAL",
  likelihood: "MEDIUM",
  impact: "MEDIUM",
  status: "IDENTIFIED",
  owner: "",
  mitigationPlan: "",
};

const AddRiskDialog = ({ open, setOpen, onSuccess, editObject }) => {
  const [data, setData] = useState(initialData);
  const [message, setMessage] = useState("");
  const [messageOpen, setMessageOpen] = useState(false);
  const [severity, setSeverity] = useState("success");
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    if (editObject) {
      setData({ ...editObject });
    } else {
      setData(initialData);
    }
    setMessageOpen(false);
  }, [editObject, open]);

  const validateForm = () => {
    if (!data.riskCode || data.riskCode.trim() === "") {
      setError("Risk code is required");
      return false;
    }
    if (!data.riskTitle || data.riskTitle.trim() === "") {
      setError("Risk title is required");
      return false;
    }
    return true;
  };

  const setError = (errorMessage) => {
    setMessage(errorMessage);
    setMessageOpen(true);
    setSeverity("error");
  };

  const setSuccess = (successMessage) => {
    setMessage(successMessage);
    setMessageOpen(true);
    setSeverity("success");
  };

  const handleSubmit = async () => {
    if (!validateForm()) return;

    setIsSubmitting(true);
    try {
      await createRisk(data);
      setSuccess("Risk created successfully");
      setTimeout(() => {
        setData(initialData);
        setMessageOpen(false);
        setOpen(false);
        onSuccess && onSuccess();
      }, 1000);
    } catch (error) {
      setError(error.message || "An error occurred");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    setData(initialData);
    setMessageOpen(false);
    setOpen(false);
  };

  const riskScore = (() => {
    const likelihoodValues = { LOW: 1, MEDIUM: 2, HIGH: 3, CRITICAL: 4 };
    const impactValues = { LOW: 1, MEDIUM: 2, HIGH: 3, CRITICAL: 4 };
    return (likelihoodValues[data.likelihood] || 1) * (impactValues[data.impact] || 1);
  })();

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>{editObject ? "Edit Risk" : "Add New Risk"}</DialogTitle>
      <DialogContent>
        <MDBox my={2} display="flex" gap={2}>
          <TextField
            id="riskCode"
            label="Risk Code"
            fullWidth
            variant="outlined"
            value={data.riskCode}
            onChange={(e) => setData({ ...data, riskCode: e.target.value })}
            required
          />
          <TextField
            id="category"
            label="Category"
            fullWidth
            select
            variant="outlined"
            value={data.category}
            onChange={(e) => setData({ ...data, category: e.target.value })}
          >
            {RISK_CATEGORIES.map((cat) => (
              <MenuItem key={cat} value={cat}>{cat}</MenuItem>
            ))}
          </TextField>
        </MDBox>
        <MDBox my={2}>
          <TextField
            id="riskTitle"
            label="Risk Title"
            fullWidth
            variant="outlined"
            value={data.riskTitle}
            onChange={(e) => setData({ ...data, riskTitle: e.target.value })}
            required
            helperText="*Required"
          />
        </MDBox>
        <MDBox my={2} display="flex" gap={2} alignItems="center">
          <TextField
            id="likelihood"
            label="Likelihood"
            fullWidth
            select
            variant="outlined"
            value={data.likelihood}
            onChange={(e) => setData({ ...data, likelihood: e.target.value })}
          >
            {SEVERITY_LEVELS.map((level) => (
              <MenuItem key={level} value={level}>{level}</MenuItem>
            ))}
          </TextField>
          <TextField
            id="impact"
            label="Impact"
            fullWidth
            select
            variant="outlined"
            value={data.impact}
            onChange={(e) => setData({ ...data, impact: e.target.value })}
          >
            {SEVERITY_LEVELS.map((level) => (
              <MenuItem key={level} value={level}>{level}</MenuItem>
            ))}
          </TextField>
          <TextField
            label="Score"
            value={riskScore}
            variant="outlined"
            disabled
            sx={{ width: 100 }}
          />
        </MDBox>
        <MDBox my={2} display="flex" gap={2}>
          <TextField
            id="owner"
            label="Risk Owner"
            fullWidth
            variant="outlined"
            value={data.owner}
            onChange={(e) => setData({ ...data, owner: e.target.value })}
          />
          <TextField
            id="status"
            label="Status"
            fullWidth
            select
            variant="outlined"
            value={data.status}
            onChange={(e) => setData({ ...data, status: e.target.value })}
          >
            {RISK_STATUSES.map((s) => (
              <MenuItem key={s} value={s}>{s}</MenuItem>
            ))}
          </TextField>
        </MDBox>
        <MDBox my={2}>
          <TextField
            id="mitigationPlan"
            label="Mitigation Plan"
            fullWidth
            multiline
            rows={2}
            variant="outlined"
            value={data.mitigationPlan}
            onChange={(e) => setData({ ...data, mitigationPlan: e.target.value })}
          />
        </MDBox>
        <MDBox my={2}>
          <TextField
            id="description"
            label="Description"
            fullWidth
            multiline
            rows={2}
            variant="outlined"
            value={data.description}
            onChange={(e) => setData({ ...data, description: e.target.value })}
          />
        </MDBox>
        <Collapse in={messageOpen}>
          <Alert
            severity={severity}
            action={
              <IconButton size="small" onClick={() => setMessageOpen(false)}>
                <CloseIcon fontSize="inherit" />
              </IconButton>
            }
          >
            {message}
          </Alert>
        </Collapse>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleSubmit} disabled={isSubmitting} variant="contained">
          {editObject ? "Update" : "Add"}
        </Button>
        <Button onClick={handleClose}>Cancel</Button>
      </DialogActions>
    </Dialog>
  );
};

export default memo(AddRiskDialog);

